import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { AnalyticsService, MAIN_TOOLBAR_EVENTS } from '@qtek/libs/analytics';
import { TASK_MYSID, TasksFeature, UserStore } from '@qtek/libs/store';
import { AppBarService, ToolbarConfig } from '@qtek/shared/services';

@Component({
  selector: 'qt-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  appBar = inject(AppBarService);
  media = inject(MediaObserver);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private store = inject(Store);
  private analytics = inject(AnalyticsService);
  private userStore = inject(UserStore);

  @Output() toggleToolbar: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleRightPanel: EventEmitter<void> = new EventEmitter<void>();
  @Output() openAiChat: EventEmitter<void> = new EventEmitter<void>();
  @Output() openTasks: EventEmitter<void> = new EventEmitter<void>();
  @Output() addNote: EventEmitter<void> = new EventEmitter<void>();
  @Output() addContact: EventEmitter<void> = new EventEmitter<void>();
  @Output() addDeal: EventEmitter<void> = new EventEmitter<void>();

  /** Whether navigation is in mini mode. */
  @Input() mini: boolean;
  @Input() isLargeScreen: boolean;
  @Input() rightPanelOpened: boolean;
  @Input() rightPanelActiveTab: 'chat' | 'tasks' | null;
  @Input() showAiChat = false;
  @Input() showAddDeal = false;

  /** Hold active toolbar config. */
  toolbarConfig: ToolbarConfig;
  readonly isParticipant = computed(() => this.userStore.relationType() === 20);

  tasksCount$: Observable<number> = this.store
    .select(TasksFeature.selectTasksByMysid(TASK_MYSID.PERSONAL_TASKS))
    .pipe(
      map(
        (tasks: any[]) => tasks.filter(task => task.status === 'active').length
      )
    );

  destroy$ = new Subject<void>();

  onAddDealClick(): void {
    this.analytics.track(MAIN_TOOLBAR_EVENTS.TOOLBAR_ADD_DEAL_CLICKED);
    this.addDeal.emit();
  }

  onOpenAiChatClick(): void {
    if (!(this.rightPanelOpened && this.rightPanelActiveTab === 'chat')) {
      this.analytics.track(MAIN_TOOLBAR_EVENTS.TOOLBAR_AI_CHAT_TOGGLED);
    }
    this.openAiChat.emit();
  }

  onOpenTasksClick(): void {
    if (!(this.rightPanelOpened && this.rightPanelActiveTab === 'tasks')) {
      this.analytics.track(MAIN_TOOLBAR_EVENTS.TOOLBAR_TASKS_PANEL_TOGGLED);
    }
    this.openTasks.emit();
  }

  onAddContactClick(): void {
    this.analytics.track(MAIN_TOOLBAR_EVENTS.TOOLBAR_ADD_CONTACT_CLICKED);
    this.addContact.emit();
  }

  onAddNoteClick(): void {
    this.analytics.track(MAIN_TOOLBAR_EVENTS.TOOLBAR_ADD_NOTE_CLICKED);
    this.addNote.emit();
  }

  ngOnInit() {
    this.appBar.configChange
      .pipe(
        filter(value => !(value === null && this.toolbarConfig === null)),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.toolbarConfig = value;

        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
